import Button from "@ui/button/Button";
import { EntityInput } from "./EntityInput";
import { askConfirm } from "./confirm";
import { useCallback, useEffect, useState } from "react";
import styled from "styled-components";
import { toast } from "react-toastify";
import Config from "src/Config";
import { superAdminFetch as fetch } from "src/services/superAdminFetch";
import { T } from "../adminTheme";
import { DangerZone, date, Paged, Fact, Facts, FilterBar, LinkButton, Muted, Page, Panel, Row, State, StatusBadge, Table } from "./adminUi";

interface NetworkState {
  chain_key: string;
  family: string;
  native_symbol: string;
  usable: boolean;
  reason: string | null;
  public_reason: string | null;
  gas_fee_cents: number;
  min_deposit_cents: number;
}

interface TreasuryState {
  chain_key: string;
  native_symbol: string;
  address: string;
  balance: string;
  balance_units: string;
  balance_cents: number;
  native_usd_units: number;
  gas_unit: "energy" | "gas";
  gas_price_units: string;
  gas_price_display: string;
  balance_gas_units: string;
  sweep_gas_units: number;
  cost_per_sweep: string;
  cost_per_sweep_units: string;
  cost_per_sweep_cents: number;
  sweeps_left: number;
  low: boolean;
  dry: boolean;
  unreadable?: boolean;
  reason: string | null;
  checked_at: string;
}

interface GasState {
  chain_key: string;
  native_symbol: string;
  native_usd_units: number;
  price_source: string;
  price_at: string;
  price_age_seconds: number;
  gas_price_units: string;
  gas_price_display: string;
  gas_limit: number;
  gas_read_at: string;
  sweep_units: string;
  sweep_native: string;
  sweep_cost_cents: number;
  markup_percent: number;
  fee_units: string;
  fee_cents: number;
  min_deposit_cents: number;
  stale: boolean;
  reason: string | null;
}

interface SystemDeposit {
  id: number;
  company_uid?: string;
  user_uid?: string;
  coin_key: string | null;
  address: string;
  tx_amount: number;
  coin_decimals: number;
  status: string;
  sweep_status: string;
  sweep_error: string | null;
  credit_error: string | null;
  received_amount?: number;
  fee_cents?: number;
  gas_fee_cents?: number;
  credited_amount?: number;
  created_at: string;
}

type AddressOwner = Pick<StoredAddress, "user_uid" | "company_uid">;

interface StoredAddress {
  id?: number;
  user_uid?: string;
  company_uid?: string;
  coin_key: string;
  address: string;
  kind: "proxy" | "issuer";
  status: "active" | "disabled";
  chain_key?: string;
  contract_address?: string | null;
  derivation_index?: number | null;
  derivation_path?: string | null;
  coin_decimals?: number;
  deposits?: number;
  last_deposit_at?: string | null;
  last_scan_at?: string | null;
  balance?: {
    native: string;
    native_cents: number;
    token_minor: number;
    error: string | null;
  };
}

interface Pagination {
  page: number;
  limit: number;
  total: number;
  pages: number;
}

interface AddressBook {
  pagination: Pagination;
  addresses: StoredAddress[];
  summary?: {
    total: number;
    by_kind: Record<string, number>;
    by_status: Record<string, number>;
    by_chain: Record<string, number>;
    balances_read_on_page: number;
    leftover_cents_on_page: Record<string, number>;
  };
}

const Address = styled.code`
  display: block; overflow-wrap: anywhere; color: ${T.text2};
`;

const formatUsdCents = (value: number | undefined) =>
  `$${((value ?? 0) / 100).toFixed(2)}`;

interface ApiPayload {
  data?: unknown;
  error?: { message?: string; details?: unknown };
}

const asPayload = (payload: unknown): ApiPayload =>
  typeof payload === "object" && payload !== null ? (payload as ApiPayload) : {};

const responseError = (payload: unknown) => {
  const error = asPayload(payload).error;
  if (!error) return null;
  const details =
    typeof error.details === "string"
      ? error.details
      : error.details
        ? JSON.stringify(error.details)
        : "";
  return [error.message, details].filter(Boolean).join(": ") || "Unknown error";
};

const unwrap = <T,>(payload: unknown): T => (asPayload(payload).data ?? payload) as T;

export const SuperAdminCrypto = ({ token, companyName, onOpenCompany }: { token: string; companyName?: (uid: string) => string | undefined; onOpenCompany?: (uid: string) => void }) => {
  const [networks, setNetworks] = useState<NetworkState[]>([]);
  const [treasury, setTreasury] = useState<TreasuryState[]>([]);
  const [gas, setGas] = useState<GasState[]>([]);
  const [deposits, setDeposits] = useState<SystemDeposit[]>([]);
  const [addresses, setAddresses] = useState<StoredAddress[]>([]);
  const [addressFilter, setAddressFilter] = useState({
    user_uid: "",
    company_uid: "",
    coin_key: "",
    chain_key: "",
    kind: "",
    status: "",
  });
  const [addressPagination, setAddressPagination] = useState<Pagination | null>(null);
  const [addressSummary, setAddressSummary] = useState<AddressBook["summary"]>(undefined);
  const [withAddressBalances, setWithAddressBalances] = useState(false);
  const [depositStatus, setDepositStatus] = useState("pending");
  const [loading, setLoading] = useState(false);
  const [lastRun, setLastRun] = useState<Record<string, number> | null>(null);
  const api = Config.scheme.URL_API;
  const hasAddressFilters = Object.values(addressFilter).some((value) => value.trim());

  const request = useCallback(
    async (
      method: string,
      options?: {
        httpMethod?: "GET" | "POST";
        body?: Record<string, unknown>;
        query?: Record<string, string>;
      },
    ) => {
      const url = new URL(api + method);
      Object.entries(options?.query ?? {}).forEach(([key, value]) => {
        if (value) url.searchParams.set(key, value);
      });
      const response = await fetch(url.toString(), {
        method: options?.httpMethod ?? "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body:
          options?.httpMethod === "POST"
            ? JSON.stringify(options.body ?? {})
            : undefined,
      });
      const payload = await response.json();
      const error = responseError(payload);
      if (!response.ok || error) throw new Error(error || response.statusText);
      return payload;
    },
    [api, token],
  );

  const loadOverview = useCallback(async (force = false) => {
    setLoading(true);
    try {
      const [networkPayload, treasuryPayload, gasPayload, depositPayload] =
        await Promise.all([
          request("system/crypto/networks"),
          request("system/crypto/treasury", { query: { force: String(force) } }),
          request("system/crypto/gas", { query: { force: String(force) } }),
          request("system/crypto/deposits", {
            query: { status: depositStatus },
          }),
        ]);
      setNetworks(unwrap<NetworkState[]>(networkPayload));
      setTreasury(unwrap<TreasuryState[]>(treasuryPayload));
      setGas(unwrap<GasState[]>(gasPayload));
      setDeposits(unwrap<SystemDeposit[]>(depositPayload));
    } catch (error) {
      console.error(error);
      toast.error(
        error instanceof Error
          ? error.message
          : "Failed to load crypto operations",
      );
    } finally {
      setLoading(false);
    }
  }, [depositStatus, request]);

  useEffect(() => {
    void loadOverview();
  }, [loadOverview]);

  const loadAddresses = async (page = 1, includeBalances = withAddressBalances) => {
    setLoading(true);
    try {
      const query: Record<string, string> = includeBalances
        ? { page: String(page), limit: "20", with_balance: "true" }
        : {
            user_uid: addressFilter.user_uid.trim(),
            company_uid: addressFilter.company_uid.trim(),
            coin_key: addressFilter.coin_key.trim(),
            chain_key: addressFilter.chain_key.trim(),
            kind: addressFilter.kind,
            status: addressFilter.status,
            page: String(page),
            limit: "100",
          };
      const payload = await request(`system/crypto/addresses${includeBalances ? "/export" : ""}`, {
        query,
      });
      const book = unwrap<AddressBook>(payload);
      setAddresses(book.addresses);
      setAddressPagination(book.pagination);
      setAddressSummary(book.summary);
    } catch (error) {
      toast.error(
        error instanceof Error ? error.message : "Failed to load addresses",
      );
    } finally {
      setLoading(false);
    }
  };

  const disableAddresses = async (
    kind: "proxy" | "issuer",
    coinKey?: string,
    owner?: AddressOwner,
  ) => {
    const target = owner ?? addressFilter;
    const userUid = target.user_uid?.trim() ?? "";
    const companyUid = target.company_uid?.trim() ?? "";
    if (!userUid && !companyUid) {
      toast.warning("Enter a user or company UID before disabling addresses");
      return;
    }
    if (
      !(await askConfirm({
        title: `Disable ${kind} address${coinKey ? ` for ${coinKey}` : "es"}?`,
        body: "Transfers remain attributable, but the next buyer request creates a fresh address.",
        confirmLabel: "Disable",
        danger: true,
      }))
    )
      return;
    setLoading(true);
    try {
      const body: Record<string, unknown> = { kind };
      if (userUid) body.user_uid = userUid;
      if (companyUid) body.company_uid = companyUid;
      if (coinKey) body.coin_key = coinKey;
      const payload = await request("system/crypto/addresses/delete", {
        httpMethod: "POST",
        body,
      });
      const result = unwrap<{ disabled: number }>(payload);
      toast.success(`${result.disabled ?? 0} address(es) disabled`);
      await loadAddresses(addressPagination?.page ?? 1);
    } catch (error) {
      toast.error(
        error instanceof Error ? error.message : "Failed to disable addresses",
      );
    } finally {
      setLoading(false);
    }
  };

  const runGasCheck = async () => {
    setLoading(true);
    try {
      const payload = await request("system/crypto/gas/check", { httpMethod: "POST" });
      setTreasury(unwrap<TreasuryState[]>(payload));
      toast.success("Gas prices and treasury balances checked");
      await loadOverview(false);
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Gas check failed");
    } finally {
      setLoading(false);
    }
  };

  const runSweep = async () => {
    setLoading(true);
    try {
      const payload = await request("system/crypto/sweep/run", {
        httpMethod: "POST",
      });
      setLastRun(unwrap<Record<string, number>>(payload));
      toast.success("Sweeper tick completed");
      await loadOverview();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Sweeper failed");
    } finally {
      setLoading(false);
    }
  };

  const depositAction = async (
    method: "sweep/retry" | "deposits/credit",
    id: number,
  ) => {
    setLoading(true);
    try {
      await request(`system/crypto/${method}`, {
        httpMethod: "POST",
        body: { id },
      });
      toast.success(
        method === "sweep/retry"
          ? "Sweep requeued"
          : "Deposit credit requested",
      );
      await loadOverview();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Operation failed");
    } finally {
      setLoading(false);
    }
  };

  const setFilter = (key: keyof typeof addressFilter, value: string) => {
    setWithAddressBalances(false);
    setAddressFilter(previous => ({ ...previous, [key]: value }));
  };
  const coinLabel = (key: string | null) => key?.replaceAll("_", " ") || "—";

  return (
    <Page>
      <Panel
        title="Deposits"
        hint="Read the sweep and credit errors before retrying. Manual actions are idempotent."
      >
        <FilterBar style={{ marginBottom: 16 }}>
          <label>Status
            <select value={depositStatus} onChange={(event) => setDepositStatus(event.target.value)}>
              <option value="pending">Pending</option>
              <option value="credited">Credited</option>
              <option value="late">Late</option>
              <option value="unmatched">Unmatched</option>
              <option value="">All</option>
            </select>
          </label>
        </FilterBar>
        <State loading={loading && !deposits.length} empty={!loading && !deposits.length} emptyText="No deposits for this filter." />
        {deposits.length > 0 && <Paged rows={deposits}>{(pageRows) => (
          <Table>
            <thead>
              <tr><th>Deposit</th><th>Status</th><th>Owner</th><th>Address</th><th>Amounts</th><th /></tr>
            </thead>
            <tbody>
              {pageRows.map((deposit) => (
                <tr key={deposit.id}>
                  <td><span style={{ fontWeight: 600 }}>#{deposit.id}</span><small>{coinLabel(deposit.coin_key)}</small></td>
                  <td>
                    <StatusBadge value={deposit.status} />
                    <small>Sweep: <StatusBadge value={deposit.sweep_status} /></small>
                    {deposit.sweep_error ? <small style={{ color: deposit.sweep_status === "failed" ? T.danger : T.warn }}>{deposit.sweep_error}</small> : null}
                    {deposit.credit_error ? <small style={{ color: T.danger }}>{deposit.credit_error}</small> : null}
                  </td>
                  <td>
                    {deposit.company_uid && onOpenCompany ? (
                      <LinkButton type="button" onClick={() => onOpenCompany(deposit.company_uid!)}>
                        {companyName?.(deposit.company_uid) ?? deposit.company_uid}
                      </LinkButton>
                    ) : (deposit.company_uid || deposit.user_uid || "Unknown owner")}
                    <small>{date(deposit.created_at)}</small>
                  </td>
                  <td><Address>{deposit.address}</Address></td>
                  <td>
                    {deposit.fee_cents !== undefined ? (
                      <>
                        <span style={{ fontWeight: 550 }}>Received {formatUsdCents(deposit.received_amount)}</span>
                        <small>
                          Fee {formatUsdCents(deposit.fee_cents)}
                          {deposit.gas_fee_cents !== undefined ? ` (network ${formatUsdCents(deposit.gas_fee_cents)})` : ""}
                          {deposit.credited_amount !== undefined ? ` · credited ${formatUsdCents(deposit.credited_amount)}` : ""}
                        </small>
                      </>
                    ) : <Muted>—</Muted>}
                  </td>
                  <td>
                    <Row style={{ justifyContent: "flex-end", flexWrap: "nowrap" }}>
                      {deposit.sweep_status === "failed" ? (
                        <Button variant="secondarySmall" disabled={loading} onClick={() => void depositAction("sweep/retry", deposit.id)}>
                          Retry sweep
                        </Button>
                      ) : null}
                      {deposit.status !== "credited" &&
                      (deposit.sweep_status === "swept" ||
                        deposit.sweep_status === "none" ||
                        Boolean(deposit.credit_error)) ? (
                        <Button variant="secondarySmall" disabled={loading} onClick={() => void depositAction("deposits/credit", deposit.id)}>
                          Credit
                        </Button>
                      ) : null}
                    </Row>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        )}</Paged>}
      </Panel>

      <Panel
        title="Network health"
        hint="Unusable networks remain visible to buyers but cannot be selected. Dry treasuries stop new address issuance and sweeps."
        actions={
          <>
            <Button variant="secondarySmall" disabled={loading} onClick={() => void loadOverview(false)}>
              Refresh cached
            </Button>
            <Button variant="secondarySmall" disabled={loading} onClick={() => void loadOverview(true)}>
              Force live refresh
            </Button>
          </>
        }
      >
        <State loading={loading && !networks.length} empty={!loading && !networks.length} emptyText="No networks reported." />
        {networks.length > 0 && (
          <Table>
            <thead>
              <tr><th>Network</th><th>State</th><th>Treasury</th><th>Gas</th><th>Fees</th></tr>
            </thead>
            <tbody>
              {networks.map((network) => {
                const wallet = treasury.find((item) => item.chain_key === network.chain_key);
                const gasState = gas.find((item) => item.chain_key === network.chain_key);
                const healthy = network.usable && !wallet?.low && !wallet?.dry;
                const asOf = wallet?.reason?.match(/keeping the reading from (.+)$/i)?.[1];
                const note = network.reason || network.public_reason || (!wallet?.unreadable && wallet?.reason);
                return (
                  <tr key={network.chain_key}>
                    <td>
                      <span style={{ fontWeight: 600 }}>{network.chain_key}</span> <Muted>· {network.native_symbol}</Muted>
                      <small style={{ overflowWrap: "anywhere" }}>{wallet?.address ?? "Buyer-funded network"}</small>
                    </td>
                    <td>
                      <StatusBadge value={wallet?.dry ? "Empty" : wallet?.unreadable ? "RPC unavailable" : healthy ? "Ready" : "Attention"} />
                      {gasState?.stale ? <small style={{ color: T.warn }}>Stale gas data{gasState.reason ? `: ${gasState.reason}` : ""}</small> : null}
                      {note ? <small style={{ color: T.danger }}>{note}</small> : null}
                    </td>
                    <td>
                      <span>
                        Treasury {wallet?.balance ?? "—"} {network.native_symbol} ·{" "}
                        {wallet ? formatUsdCents(wallet.balance_cents) : "—"} ·{" "}
                        {wallet?.sweeps_left ?? "—"} sweeps left
                      </span>
                      {wallet?.unreadable && <small>RPC node is not responding · {asOf ? `Data as of ${asOf}` : "Last successful reading"}</small>}
                    </td>
                    <td>
                      {wallet?.gas_price_display ?? gasState?.gas_price_display ?? "Gas price unavailable"} ·{" "}
                      {wallet ? `${wallet.balance_gas_units} ${wallet.gas_unit}` : "—"}
                    </td>
                    <td>
                      Buyer fee {formatUsdCents(network.gas_fee_cents)} · minimum {formatUsdCents(network.min_deposit_cents)}
                      {gasState ? <small>Sweep {formatUsdCents(gasState.sweep_cost_cents)} + {gasState.markup_percent}% markup</small> : null}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </Table>
        )}
      </Panel>

      <Panel
        title="Addresses"
        hint="Browse the full paginated address book. Balance audit reads the current page from-chain and can take longer."
      >
        <FilterBar>
          <label>User<EntityInput token={token} kind="user" value={addressFilter.user_uid} onChange={(value) => setFilter("user_uid", value)} /></label>
          <label>Company<EntityInput token={token} kind="company" value={addressFilter.company_uid} onChange={(value) => setFilter("company_uid", value)} /></label>
          <label>Coin<select value={addressFilter.coin_key} onChange={(event) => setFilter("coin_key", event.target.value)}><option value="">Any coin</option>{[...new Set(["USDT", "USDC", ...deposits.map((item) => item.coin_key), ...addresses.map((item) => item.coin_key)])].filter((coin): coin is string => Boolean(coin)).map((coin) => <option key={coin} value={coin}>{coin}</option>)}</select></label>
          <label>Network<select value={addressFilter.chain_key} onChange={(event) => setFilter("chain_key", event.target.value)}><option value="">Any network</option>{[...new Set([...networks.map((item) => item.chain_key), ...addresses.map((item) => item.chain_key ?? "")])].filter(Boolean).map((chain) => <option key={chain} value={chain}>{chain}</option>)}</select></label>
          <label>Kind
            <select value={addressFilter.kind} onChange={(event) => setFilter("kind", event.target.value)}>
              <option value="">All</option><option value="proxy">Proxy</option><option value="issuer">Issuer</option>
            </select>
          </label>
          <label>Status
            <select value={addressFilter.status} onChange={(event) => setFilter("status", event.target.value)}>
              <option value="">All</option><option value="active">Active</option><option value="disabled">Disabled</option>
            </select>
          </label>
        </FilterBar>
        <Row style={{ marginTop: 16, justifyContent: "space-between" }}>
          <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13.5, color: T.text2 }}>
            <input type="checkbox" style={{ accentColor: T.accent, width: 15, height: 15, margin: 0 }} checked={withAddressBalances} disabled={hasAddressFilters} onChange={event => setWithAddressBalances(event.target.checked)} />
            Audit on-chain balances for this page
          </label>
          <Button variant="primarySmall" disabled={loading} onClick={() => void loadAddresses(1)}>
            Load addresses
          </Button>
        </Row>
        {addressSummary ? (
          <Facts style={{ marginTop: 14 }}>
            <Fact label="Total addresses">{addressSummary.total}</Fact>
            <Fact label="Balances read on this page">{addressSummary.balances_read_on_page}</Fact>
            <Fact label="Leftover gas on this page">{Object.entries(addressSummary.leftover_cents_on_page).map(([chain, cents]) => `${chain} ${formatUsdCents(cents)}`).join(" · ") || "none"}</Fact>
          </Facts>
        ) : null}
        {addresses.length > 0 && (
          <Table style={{ marginTop: 16 }}>
            <thead>
              <tr><th>Coin</th><th>Kind</th><th>Status</th><th>Address</th><th>Activity</th><th>Balance</th><th /></tr>
            </thead>
            <tbody>
              {addresses.map((item, index) => (
                <tr key={item.id ?? `${item.address}-${index}`}>
                  <td>{coinLabel(item.coin_key)}</td>
                  <td style={{ textTransform: "capitalize" }}>{item.kind}</td>
                  <td><StatusBadge value={item.status} /></td>
                  <td>
                    <Address>{item.address}</Address>
                    <small>{item.chain_key || "—"} · path {item.derivation_path ?? item.derivation_index ?? "—"} · {item.coin_decimals ?? "—"} decimals</small>
                  </td>
                  <td>
                    {item.deposits ?? 0} deposits
                    <small>Last deposit {item.last_deposit_at ? date(item.last_deposit_at) : "never"}</small>
                    <small>Scanned {item.last_scan_at ? date(item.last_scan_at) : "never"}</small>
                  </td>
                  <td>
                    {item.balance ? (
                      <span style={{ color: item.balance.error ? T.danger : undefined }}>
                        Native {item.balance.native} ({formatUsdCents(item.balance.native_cents)}) · token units {item.balance.token_minor}{item.balance.error ? ` · ${item.balance.error}` : ""}
                      </span>
                    ) : <Muted>—</Muted>}
                  </td>
                  <td style={{ textAlign: "right" }}>
                    {item.status === "active" ? (
                      <Button
                        variant="secondarySmall"
                        disabled={loading}
                        onClick={() =>
                          void disableAddresses(item.kind, item.coin_key, {
                            user_uid: item.user_uid,
                            company_uid: item.company_uid,
                          })
                        }
                      >
                        Disable
                      </Button>
                    ) : null}
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        )}
        {!addresses.length && addressPagination ? <State empty emptyText="No addresses for this filter." /> : null}
        {addressPagination ? (
          <Row style={{ justifyContent: "space-between", marginTop: 16, paddingTop: 14, borderTop: `1px solid ${T.border}` }}>
            <Muted>Page {addressPagination.page} of {Math.max(1, addressPagination.pages)} · {addressPagination.total} addresses</Muted>
            <Row>
              <Button variant="secondarySmall" disabled={loading || addressPagination.page <= 1} onClick={() => void loadAddresses(addressPagination.page - 1)}>Previous</Button>
              <Button variant="secondarySmall" disabled={loading || addressPagination.page >= addressPagination.pages} onClick={() => void loadAddresses(addressPagination.page + 1)}>Next</Button>
            </Row>
          </Row>
        ) : null}
      </Panel>

      <DangerZone title="Engineering tools" hint="For developers. Do not use during support work.">
        <Panel title="Gas check" hint="Re-reads gas prices and treasury balances from the chains.">
          <Row><Button variant="primarySmall" disabled={loading} onClick={() => void runGasCheck()}>Run gas check</Button></Row>
        </Panel>
        <Panel title="Sweeper" hint="Runs one sweeper tick now instead of waiting for the schedule.">
          <Row><Button variant="primarySmall" disabled={loading} onClick={runSweep}>Run sweeper</Button></Row>
          {lastRun ? (
            <Muted style={{ display: "block", marginTop: 10 }}>
              Last run:{" "}
              {Object.entries(lastRun)
                .map(([key, value]) => `${key.replaceAll("_", " ")} ${value}`)
                .join(" · ")}
            </Muted>
          ) : null}
        </Panel>
        <Panel title="Legacy addresses" hint="Disables every legacy issuer address for the user or company UID entered in the Addresses filter above.">
          <Row><Button variant="secondarySmall" disabled={loading} onClick={() => void disableAddresses("issuer")}>Clear legacy issuer addresses</Button></Row>
        </Panel>
      </DangerZone>
    </Page>
  );
};
